// Name-day engine (region pack: GR/CY Orthodox).
// Replaces the 8.9 MB pre-expanded NAMEDAY_DAILY_DB / NAMEDAY_MATCH_DB tables from
// v910 with 233 rules (fixed dates, Pascha offsets, St George, St Chloe) that
// regenerate the legacy tables exactly (verified by tools/namedays/extract_rules.py
// and tests/unit/namedays.test.ts) — and keep working after 2126.
import rulesJson from './gr-cy-orthodox.json' with { type: 'json' };
import { addDays, type Day } from '../dates.ts';

type Rule =
  | { type: 'fixed'; month: number; day: number }
  | { type: 'pascha'; offset: number }
  | { type: 'stGeorge' }
  | { type: 'sundayOnOrAfter'; month: number; day: number };

export interface NameDayRule {
  code: string;
  feast: string;
  rule: Rule;
  names: { key: string; display: string; confidence: string }[];
}

const RULES = (rulesJson as { rules: NameDayRule[] }).rules;

/** Uppercase, accent-free key used for matching (same normalisation as legacy v910). */
export function nameKey(s: string): string {
  return String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase().trim();
}

const BY_KEY = new Map<string, NameDayRule[]>();
for (const r of RULES) for (const n of r.names) {
  const list = BY_KEY.get(n.key) ?? [];
  list.push(r);
  BY_KEY.set(n.key, list);
}

/** Orthodox Pascha (Gregorian calendar date), Meeus Julian algorithm + JDN conversion. */
export function orthodoxEaster(year: number): Day {
  const a = year % 4, b = year % 7, c = year % 19;
  const d = (19 * c + 15) % 30;
  const e = (2 * a + 4 * b - d + 34) % 7;
  const month = Math.floor((d + e + 114) / 31);
  const day = ((d + e + 114) % 31) + 1;
  const a2 = Math.floor((14 - month) / 12);
  const yy = year + 4800 - a2;
  const mm = month + 12 * a2 - 3;
  const jdn = day + Math.floor((153 * mm + 2) / 5) + 365 * yy + Math.floor(yy / 4) - 32083;
  // JDN → Gregorian
  const f = jdn + 1401 + Math.floor((Math.floor((4 * jdn + 274277) / 146097) * 3) / 4) - 38;
  const e2 = 4 * f + 3;
  const g = Math.floor((e2 % 1461) / 4);
  const h = 5 * g + 2;
  const D = Math.floor((h % 153) / 5) + 1;
  const M = ((Math.floor(h / 153) + 2) % 12) + 1;
  const Y = Math.floor(e2 / 1461) - 4716 + Math.floor((12 + 2 - M) / 12);
  return `${Y}-${String(M).padStart(2, '0')}-${String(D).padStart(2, '0')}`;
}

function pad(n: number) { return String(n).padStart(2, '0'); }

export function ruleDate(rule: Rule, year: number): Day | null {
  switch (rule.type) {
    case 'fixed': {
      if (rule.month === 2 && rule.day === 29 && !(year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0))) return null;
      return `${year}-${pad(rule.month)}-${pad(rule.day)}`;
    }
    case 'pascha': return addDays(orthodoxEaster(year), rule.offset);
    case 'stGeorge': {
      const fixed = `${year}-04-23`;
      const after = addDays(orthodoxEaster(year), 1);
      return fixed > after ? fixed : after;
    }
    case 'sundayOnOrAfter': {
      const d = `${year}-${pad(rule.month)}-${pad(rule.day)}`;
      const wd = new Date(Date.UTC(year, rule.month - 1, rule.day)).getUTCDay();
      return addDays(d, (7 - wd) % 7);
    }
  }
}

export interface NameDayHit { date: Day; feast: string; code: string; names: string[] }

/** All feasts and celebrating names on a given day. */
export function nameDaysOn(day: Day): NameDayHit[] {
  const year = Number(day.slice(0, 4));
  const out: NameDayHit[] = [];
  for (const r of RULES) {
    if (ruleDate(r.rule, year) === day) out.push({ date: day, feast: r.feast, code: r.code, names: r.names.map((n) => n.display) });
  }
  return out;
}

/** Map of name-key → feast for a day (equivalent of legacy NAMEDAY_MATCH_DB[day]). */
export function nameMatchesOn(day: Day): Map<string, string> {
  const year = Number(day.slice(0, 4));
  const m = new Map<string, string>();
  for (const r of RULES) if (ruleDate(r.rule, year) === day) for (const n of r.names) if (!m.has(n.key)) m.set(n.key, r.feast);
  return m;
}

/**
 * Next name day (>= from) for a first name. `manual` (MM-DD) overrides the rule table,
 * exactly like the legacy per-client `nameDay` field.
 */
export function nextNameDay(firstName: string, from: Day, manual?: string | null): { date: Day; feast: string } | null {
  if (manual && /^\d{2}-\d{2}$/.test(manual)) {
    const y = Number(from.slice(0, 4));
    for (const yy of [y, y + 1]) { const d = `${yy}-${manual}`; if (d >= from) return { date: d, feast: '' }; }
  }
  const rules = BY_KEY.get(nameKey(firstName));
  if (!rules?.length) return null;
  const y = Number(from.slice(0, 4));
  let best: { date: Day; feast: string } | null = null;
  for (const yy of [y, y + 1]) for (const r of rules) {
    const d = ruleDate(r.rule, yy);
    if (d && d >= from && (!best || d < best.date)) best = { date: d, feast: r.feast };
  }
  return best;
}

export const NAMEDAY_RULE_COUNT = RULES.length;
